import { Box, Divider, Drawer, Stack } from '@mui/material'
import React from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import CheckboxController from '@/components/inputs/CheckboxController'
import SelectFieldController from '@/components/inputs/SelectFieldController'
import TextFieldController from '@/components/inputs/TextFieldController'
import { ConfigField } from '@/features/controller/configuration/components/drawers/ConfigField'
import { DrawerActions } from '@/features/controller/configuration/components/drawers/DrawerActions'
import { DrawerDescription } from '@/features/controller/configuration/components/drawers/DrawerDescription'
import { DrawerTitle } from '@/features/controller/configuration/components/drawers/DrawerTitle'
import { CONFIG_FIELD_NUMBER_SMALL_SX } from '@/features/controller/configuration/constants'
import type { PowerMeter, PowerValueInterpretation } from '@/features/controller/configuration/models/appConfigSchema'
import { PowerMeterModel } from '@/features/controller/configuration/models/appConfigSchema'
import type { FlexibleConfigDrawerProps } from '@/features/controller/configuration/types'

const POWER_VALUES_IDENTITY: PowerValueInterpretation = {
  absolute: false,
  adjustBy: 0,
  multiplyBy: 1,
}

// AccuvimII has bus-attached required fields (busRef, modbusUnitId) that this
// drawer doesn't collect, so it isn't offered here.
type SelectablePowerMeterModel = Exclude<PowerMeterModel, PowerMeterModel.AccuvimII>

interface AppConfigPowerMeterForm {
  model: SelectablePowerMeterModel
  movingAverage: string
  valuesInterpretation: boolean
}

export function AppConfigPowerMeterDrawer({
  onClose,
  onConfigUpdated,
  onFinishedClosing,
  open,
}: Readonly<FlexibleConfigDrawerProps<PowerMeter | undefined, Omit<PowerMeter, 'id'>>>) {
  const { t } = useTranslation()
  const form = useForm<AppConfigPowerMeterForm>({
    defaultValues: {
      model: PowerMeterModel.Generic,
      movingAverage: '',
      valuesInterpretation: false,
    },
  })

  function onSave(updatedConfig: AppConfigPowerMeterForm) {
    const { model, movingAverage, valuesInterpretation } = updatedConfig
    onConfigUpdated({
      model,
      movingAverage: movingAverage ? { windowPeriod: `${movingAverage} s` } : undefined,
      // If the user chooses to configure this, we provide JSON structure defaulting to identity (no change)
      values: valuesInterpretation ? POWER_VALUES_IDENTITY : undefined,
    })
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 580 },
        },
      }}
      onClose={onClose}
      onTransitionExited={onFinishedClosing}
    >
      <FormProvider {...form}>
        <DrawerTitle title={t('controllers.configuration.power_meters.other.dialog.title')} onClose={onClose} />

        <Divider />

        <DrawerDescription description={t('controllers.configuration.power_meters.other.dialog.description')} />

        {/* Content area */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'auto',
          }}
        >
          <Stack direction="column" padding={2} spacing={2}>
            <ConfigField
              helpTooltip={t('controllers.configuration.power_meters.other.dialog.model_hint')}
              label={t('controllers.configuration.power_meters.other.dialog.model')}
            >
              <SelectFieldController
                name="model"
                options={Object.values(PowerMeterModel)
                  .filter((model) => model !== PowerMeterModel.AccuvimII)
                  .map((model) => ({
                    id: model,
                    value: model,
                    label: model,
                  }))}
              />
            </ConfigField>
            <ConfigField
              helpTooltip={t('controllers.configuration.power_meters.other.dialog.moving_average_hint')}
              label={t('controllers.configuration.power_meters.other.dialog.moving_average')}
            >
              <Stack alignItems="center" direction="row" gap={1}>
                {t('controllers.configuration.power_meters.other.dialog.moving_average_label')}
                <TextFieldController name="movingAverage" sx={CONFIG_FIELD_NUMBER_SMALL_SX} type="number" />
                {t('controllers.configuration.power_meters.other.dialog.moving_average_seconds')}
              </Stack>
            </ConfigField>
            <ConfigField
              helpTooltip={t('controllers.configuration.power_meters.other.dialog.values_hint')}
              label={t('controllers.configuration.power_meters.other.dialog.values')}
            >
              <CheckboxController
                label={t('controllers.configuration.power_meters.other.dialog.values_label')}
                name="valuesInterpretation"
              />
            </ConfigField>
          </Stack>
        </Box>

        <Divider />

        {/* Footer actions */}
        <DrawerActions hasChanges={true} onClose={onClose} onSave={onSave} />
      </FormProvider>
    </Drawer>
  )
}
